import React, { useEffect, useState } from "react";
import {
  Box, Card, Chip, CircularProgress, MenuItem, Select, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField,
  Tooltip, Typography,
} from "@mui/material";
import {
  useLazyGetSupplierSyncRunsQuery,
  useGetSupplierSyncSummaryQuery,
} from "../../../../../Services/SupplierService";

interface Props {
  supplierId: string;
  /**
   * Set when this tab is opened from a sub-supplier account rather than the
   * supplier itself. The runs below stay parent-level -- the nightly sync only
   * records them that way -- but the product count must not, or the page states
   * the supplier's total under the heading of one account.
   */
  subSupplierId?: string;
  supplierName?: string;
}

/** Local time, to the minute. The times matter more than the dates here. */
const when = (d?: string) =>
  d
    ? new Date(d).toLocaleString(undefined, {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      })
    : "—";

const duration = (ms?: number) => {
  if (ms === null || ms === undefined) return "—";
  if (ms < 1000) return `${ms} ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${Math.round(s - m * 60)}s`;
};

/**
 * Make an error readable.
 *
 * Suppliers return whole HTML pages as errors -- PJ Telesoft's block page is a
 * full document, styles and all -- which fills the cell with markup and CSS and
 * buries the one line that matters. Stripping tags alone is not enough: the
 * stylesheet survives as text. For an HTML error the <title> is the summary the
 * page was built to give ("Blocked"), so use it.
 */
const readable = (text?: string) => {
  if (!text) return "";
  const raw = String(text);

  if (/<html|<!DOCTYPE/i.test(raw)) {
    const prefix = raw.match(/^\s*([^<]{1,60}?)\s*(?=<)/)?.[1]?.trim();  // e.g. "HTTP 503:"
    const title = raw.match(/<title[^>]*>([^<]+)<\/title>/i)?.[1]?.trim();
    if (prefix || title) return [prefix, title].filter(Boolean).join(" ");
  }

  return raw
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const STATUS_STYLE: Record<string, { bg: string; fg: string; label: string }> = {
  running: { bg: "rgba(2,136,209,0.12)", fg: "#0277bd", label: "Running…" },
  success: { bg: "rgba(46,125,50,0.12)", fg: "#2e7d32", label: "Succeeded" },
  failed:  { bg: "rgba(211,47,47,0.12)", fg: "#c62828", label: "Failed" },
  skipped: { bg: "rgba(0,0,0,0.06)",     fg: "#616161", label: "Skipped" },
};

/** A change that reads at a glance: +12 in green, −3 in amber, 0 muted. */
const Delta = ({ value, good }: { value: number; good: boolean }) => {
  if (!value) return <Typography component="span" sx={{ color: "text.disabled", fontSize: 13 }}>—</Typography>;
  return (
    <Typography component="span" sx={{ fontSize: 13, fontWeight: 600, color: good ? "success.main" : "warning.main" }}>
      {good ? "+" : "−"}{Math.abs(value)}
    </Typography>
  );
};

const SyncHistory: React.FC<Props> = ({ supplierId, supplierName, subSupplierId }) => {
  const [trigger, { data, isFetching }] = useLazyGetSupplierSyncRunsQuery();
  const { data: summary } = useGetSupplierSyncSummaryQuery();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [status, setStatus] = useState("");
  const [runType, setRunType] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  useEffect(() => {
    trigger({
      page: page + 1, pageSize,
      supplierId: supplierId || undefined,
      status: status || undefined,
      trigger: runType || undefined,
      from: from || undefined,
      to: to || undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierId, page, pageSize, status, runType, from, to]);

  const rows: any[] = data?.data || [];
  const mine = (summary?.data?.perSupplier || []).find((s: any) => s._id === supplierId);

  /*
   * How many products this page is actually about.
   *
   * On an account page that is the account's own count, taken from what it
   * holds. On the supplier page it stays the run's figure, which is the
   * supplier-wide number and correct there.
   */
  const activeProducts = subSupplierId
    ? summary?.data?.activeByAccount?.[subSupplierId]
    : mine?.lastProductsActive;
  // This supplier's own figures, not every supplier's. An "all suppliers" box
  // on one supplier's tab reads as noise and invites the question of why it is
  // there at all.
  const c =
    summary?.data?.last24hBySupplier?.[supplierId] ||
    { running: 0, success: 0, failed: 0, skipped: 0 };

  const resetPage = (fn: (v: any) => void) => (v: any) => { setPage(0); fn(v); };

  return (
    <Box>
      {/* Health first: the question is "is the sync working?", not "what happened at 06:21". */}
      <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: "wrap", gap: 1.5 }}>
        <Card variant="outlined" sx={{ px: 2, py: 1.25, minWidth: 170 }}
          title="The most recent sync for this supplier, scheduled or manual.">
          <Typography sx={{ fontSize: 11, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.4 }}>
            Last run
          </Typography>
          <Typography sx={{ fontSize: 15, fontWeight: 700 }}>{when(mine?.lastRunAt)}</Typography>
          {mine?.lastStatus && (
            <Typography sx={{ fontSize: 12, color: STATUS_STYLE[mine.lastStatus]?.fg }}>
              {STATUS_STYLE[mine.lastStatus]?.label}
              {mine.lastDurationMs != null ? ` · ${duration(mine.lastDurationMs)}` : ""}
            </Typography>
          )}
        </Card>
        <Card variant="outlined" sx={{ px: 2, py: 1.25, minWidth: 150 }}
          title={subSupplierId
            ? "Products this account is currently listing. Counted from what the account holds, not from the supplier's nightly run, which covers every account at once."
            : "Products this supplier is currently listing, counted after the last run. These are the ones that can be mapped and sold."}>
          <Typography sx={{ fontSize: 11, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.4 }}>
            Active products
          </Typography>
          <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
            {activeProducts ?? "—"}
          </Typography>
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            {subSupplierId ? "this account" : "after the last run"}
          </Typography>
        </Card>
        <Card variant="outlined" sx={{ px: 2, py: 1.25, minWidth: 210 }}
          title="Sync RUNS for this supplier in the last 24 hours — not products. Normally one scheduled run per night, plus any manual ones.">
          <Typography sx={{ fontSize: 11, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.4 }}>
            Runs · last 24h
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: "wrap", gap: 0.5 }}>
            {c.running > 0 && (
              <Chip size="small" label={`${c.running} running`} sx={{ bgcolor: STATUS_STYLE.running.bg, color: STATUS_STYLE.running.fg, fontWeight: 600 }} />
            )}
            <Chip size="small" label={`${c.success} ok`} sx={{ bgcolor: STATUS_STYLE.success.bg, color: STATUS_STYLE.success.fg, fontWeight: 600 }} />
            <Chip size="small" label={`${c.failed} failed`} sx={{ bgcolor: STATUS_STYLE.failed.bg, color: STATUS_STYLE.failed.fg, fontWeight: 600 }} />
            {c.skipped > 0 && (
              <Chip size="small" label={`${c.skipped} skipped`} sx={{ bgcolor: STATUS_STYLE.skipped.bg, color: STATUS_STYLE.skipped.fg, fontWeight: 600 }} />
            )}
          </Stack>
        </Card>
      </Stack>

      <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: "wrap", gap: 1.5 }}>
        <Select size="small" displayEmpty value={status} onChange={(e) => resetPage(setStatus)(e.target.value)} sx={{ minWidth: 140 }}>
          <MenuItem value="">All statuses</MenuItem>
          <MenuItem value="running">Running</MenuItem>
          <MenuItem value="success">Succeeded</MenuItem>
          <MenuItem value="failed">Failed</MenuItem>
          <MenuItem value="skipped">Skipped</MenuItem>
        </Select>
        <Select size="small" displayEmpty value={runType} onChange={(e) => resetPage(setRunType)(e.target.value)} sx={{ minWidth: 150 }}>
          <MenuItem value="">Scheduled and manual</MenuItem>
          <MenuItem value="cron">Scheduled only</MenuItem>
          <MenuItem value="manual">Manual only</MenuItem>
        </Select>
        <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={from} onChange={(e) => resetPage(setFrom)(e.target.value)} />
        <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={to} onChange={(e) => resetPage(setTo)(e.target.value)} />
        {/*
          A free-text search sat here. Removed: the whole history holds 14
          distinct error messages, so Status = Failed plus a date range already
          surfaces every one of them, and a supplier has two or three accounts
          which are visible in the Account column anyway. It would only earn its
          place at thousands of runs. The API still accepts a `search`
          parameter if that day comes.
        */}
      </Stack>

      <TableContainer component={Card} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ "& th": { fontWeight: 700, whiteSpace: "nowrap", bgcolor: "#f7f8fa" } }}>
              {/*
                Every column explains itself on hover. These are exactly the
                questions people ask the first time they read this table —
                "why are 3 inactive?" — and the answer belongs on the screen,
                not in a conversation with whoever built it.
              */}
              {([
                ["When", "When this run started."],
                ["Run", "Scheduled = the nightly job. Manual = someone pressed Get Latest Products."],
                ["Account", "Which supplier account was synced. \u201cAll accounts\u201d means every account for this supplier."],
                ["Status", "Running = still in progress. Succeeded = finished without error. Failed = the supplier could not be reached or rejected the request."],
              ] as const).map(([label, tip]) => (
                <Tooltip key={label} title={tip}>
                  <TableCell sx={{ cursor: "help" }}>{label}</TableCell>
                </Tooltip>
              ))}
              {([
                ["Duration", "How long this run took."],
                ["Products", "How many products this supplier has in our catalogue after the run \u2014 active and inactive together."],
                ["Active", "Products the supplier is currently listing, so they can be mapped and sold."],
                ["Inactive", "Products the supplier has stopped listing. They are kept, not deleted, and come back automatically if the supplier lists them again. Some inactive products are normal \u2014 every catalogue has retired lines."],
                ["Added", "New products that appeared in this run."],
                ["Withdrawn", "Products the supplier stopped listing during THIS run. Unlike Inactive, which is the running total, this is what changed today \u2014 the number worth watching."],
                ["Providers", "How many providers (networks or brands) this supplier offers."],
              ] as const).map(([label, tip]) => (
                <Tooltip key={label} title={tip}>
                  <TableCell align="right" sx={{ cursor: "help" }}>{label}</TableCell>
                </Tooltip>
              ))}
              <Tooltip title="Why a run failed, or whatever the supplier reported. Empty when a run completed with nothing to say.">
                <TableCell sx={{ cursor: "help" }}>Message / error</TableCell>
              </Tooltip>
            </TableRow>
          </TableHead>
          <TableBody>
            {isFetching && (
              <TableRow><TableCell colSpan={12} align="center" sx={{ py: 4 }}><CircularProgress size={22} /></TableCell></TableRow>
            )}
            {!isFetching && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={12} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  No sync runs match these filters.
                </TableCell>
              </TableRow>
            )}
            {!isFetching && rows.map((r) => {
              const st = STATUS_STYLE[r.status] || STATUS_STYLE.skipped;
              return (
                <TableRow key={r._id} hover>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{when(r.startedAt)}</TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" label={r.trigger === "manual" ? "Manual" : "Scheduled"} sx={{ fontSize: 11 }} />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{r.subSupplierId || "All accounts"}</TableCell>
                  <TableCell>
                    <Chip size="small" label={st.label} sx={{ bgcolor: st.bg, color: st.fg, fontWeight: 600 }} />
                  </TableCell>
                  <TableCell align="right">
                    {r.status === "running" ? "in progress" : duration(r.durationMs)}
                  </TableCell>
                  <TableCell align="right">{r.productsAfter || 0}</TableCell>
                  <TableCell align="right" sx={{ color: "success.main", fontWeight: 600 }}>{r.productsActive || 0}</TableCell>
                  <TableCell align="right" sx={{ color: r.productsInactive ? "warning.main" : "text.disabled" }}>{r.productsInactive || 0}</TableCell>
                  <TableCell align="right"><Delta value={r.productsAdded || 0} good /></TableCell>
                  <TableCell align="right"><Delta value={r.productsDeactivated || 0} good={false} /></TableCell>
                  <TableCell align="right">{r.providersCount || 0}</TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>
                    <Tooltip title={readable(r.error || r.message) || ""}>
                      <Typography noWrap sx={{ fontSize: 12, color: r.error ? "error.main" : "text.secondary" }}>
                        {readable(r.error || r.message) || "—"}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={data?.totalRecords || 0}
          page={page}
          onPageChange={(_e, p) => setPage(p)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>

      <Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: 1.5 }}>
        A run appears as soon as it starts. Large catalogues take a few minutes —
        DTone pages through its products a hundred at a time — so a row may sit at
        &ldquo;Running&rdquo; for a while, and its figures fill in when it finishes.
        Counts are measured against the catalogue before and after each run, so they
        describe what actually changed. Runs before 31 Aug 2026 were recovered from the
        old error log and have no counts — only failures were recorded then.
      </Typography>
    </Box>
  );
};

export default SyncHistory;
